/**
 * The contacts screen — a search WORKBENCH as of Step 5.
 *
 * Three things changed from the Step 3 table, and each is a different idea:
 *
 * 1. **Filter state lives in the URL** (`src/lib/filters.js`). A filtered view
 *    is something people send each other, and the back button should undo a
 *    drill-down. It also means the CSV export and campaign creation take the
 *    same filter object the table is showing — not a re-derivation of it.
 *
 * 2. **"Select all matching (N)" sends the FILTER, not ids.** The client never
 *    held those 4,812 ids; it only ever saw one page. The server re-resolves
 *    and refuses above the cap.
 *
 * 3. **Facet counts** beside every option, which is what makes a filter panel
 *    worth more than a search box: you can see what is there before clicking.
 *
 * Dense by doctrine, as before: the results body is the only scroll region.
 */
import { useApolloClient, useQuery } from '@apollo/client';
import {
  Button,
  EmptyState,
  Spinner,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  TagChip,
} from '@meshhub/design-tokens/ui';
import { Download, Filter, MessageSquare, Plus, Trash2 } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BulkBar } from '../components/search/BulkBar.web.jsx';
import { FilterPanel } from '../components/search/FilterPanel.web.jsx';
import { SavedViewBar } from '../components/search/SavedViews.web.jsx';
import { ContactPhoto } from '../components/ContactPhoto.web.jsx';
import { MutationError } from '../components/FormKit.web.jsx';
import { DeleteContactDialog } from '../components/contact/DeleteContactDialog.web.jsx';
import { QuickAddDialog } from '../components/QuickAdd.web.jsx';
import { PAGE_SIZE, SEARCH_DEBOUNCE_MS, SELECTION_FETCH_PAGE_SIZE } from '../constants/density.js';
import { getAccessToken } from '../lib/auth.jsx';
import { formatCount, formatDate, formatPhone } from '../lib/format.js';
import {
  filterFromSearchParams,
  filterToSearchParams,
  toFilterInput,
} from '../lib/filters.js';
import {
  ADMIN_TAGS_QUERY,
  ADMIN_LOOKUPS_QUERY,
  SAVED_VIEWS_QUERY,
  SEARCH_CONTACTS_QUERY,
} from '../lib/queries.js';
import { BFF_ORIGIN } from '../lib/runtimeEnv.js';
import { useSession } from '../lib/session.jsx';
import { useShortcut } from '../lib/shortcuts.jsx';
import { S } from '../lib/strings.js';

export function ContactsScreen() {
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const { isAdmin } = useSession();
  const [params, setParams] = useSearchParams();

  // The URL is the source of truth for the filter; `draft` is what the panel
  // edits between keystrokes. One debounce, on the way OUT to the URL, so
  // typing stays instant and the address bar does not thrash.
  const urlFilter = useMemo(() => filterFromSearchParams(params), [params]);
  const [draft, setDraft] = useState(urlFilter);
  const [page, setPage] = useState(Number(params.get('page') ?? 1));
  const [showFilters, setShowFilters] = useState(true);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  // The CONTACTS picked, not only their ids, because a selection outlives the
  // search that found them: pick one person, search for another, pick them
  // too. The objects are what let the send dialog preview against somebody
  // who is actually selected once they have scrolled out of the result set.
  const [selected, setSelected] = useState([]);
  const [allMatching, setAllMatching] = useState(false);
  // True while «all matching» is being turned into a real id list (below).
  const [materialising, setMaterialising] = useState(false);
  // The row whose trash icon was clicked, and a refusal from deleting it.
  const [deleting, setDeleting] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const selectedIds = useMemo(() => selected.map((c) => c.id), [selected]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const next = filterToSearchParams(draft, { page: 1 });
      setParams(next, { replace: true });
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // `draft` only. Reacting to `params` too would fight the effect that wrote
    // them and loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  useShortcut('/', () => searchRef.current?.focus(), {
    description: S.shortcuts.actions.searchContacts,
    group: S.shortcuts.groups.contacts,
  });
  useShortcut('n', () => setQuickAddOpen(true), {
    description: S.shortcuts.actions.quickAdd,
    group: S.shortcuts.groups.contacts,
  });
  useShortcut('f', () => setShowFilters((v) => !v), {
    description: S.search.filters,
    group: S.shortcuts.groups.contacts,
  });

  const filterInput = useMemo(() => toFilterInput(urlFilter), [urlFilter]);

  const { data, loading, error, refetch } = useQuery(SEARCH_CONTACTS_QUERY, {
    variables: { filter: filterInput, page, pageSize: PAGE_SIZE, withFacets: true },
  });
  const { data: tagData } = useQuery(ADMIN_TAGS_QUERY, {
    variables: { includeInactive: false },
    errorPolicy: 'ignore',
  });
  // The vocabularies the filter panel offers, fetched with one explicit hook
  // EACH rather than a loop: `useQuery` inside a `.map()` works only while the
  // array is constant, and a rule that holds by accident is a rule that breaks
  // the first time somebody makes it configurable. `useVocabulary` is a
  // one-line wrapper, not a loop — the call sites stay explicit and countable.
  //
  // Nine queries, and Apollo dedupes each against the identical one the
  // contact form runs, so opening the panel beside a dialog is not eighteen.
  const useVocabulary = (kind) =>
    useQuery(ADMIN_LOOKUPS_QUERY, {
      variables: { kind, includeInactive: false },
      errorPolicy: 'ignore',
    }).data?.lookups ?? [];

  const lookups = {
    FIELD_OF_WORK: useVocabulary('FIELD_OF_WORK'),
    SPECIALTY: useVocabulary('SPECIALTY'),
    PRODUCT: useVocabulary('PRODUCT'),
    // --- the CRM profile dimensions (migration 0016) ---
    LIFECYCLE_STAGE: useVocabulary('LIFECYCLE_STAGE'),
    SALES_STAGE: useVocabulary('SALES_STAGE'),
    CUSTOMER_HEALTH: useVocabulary('CUSTOMER_HEALTH'),
    ENGAGEMENT_STATUS: useVocabulary('ENGAGEMENT_STATUS'),
    CITY: useVocabulary('CITY'),
    FACILITY_TYPE: useVocabulary('FACILITY_TYPE'),
  };
  const { data: viewData, refetch: refetchViews } = useQuery(SAVED_VIEWS_QUERY, {
    errorPolicy: 'ignore',
  });

  const contacts = data?.searchContacts?.items ?? [];
  const totalCount = data?.searchContacts?.totalCount ?? 0;
  const facets = data?.searchContacts?.facets ?? [];
  const pageCount = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const bulkLimit = viewData?.bulkLimit ?? 5000;

  // A new filter drops «all matching» — that selection IS the old filter —
  // but keeps the rows ticked one by one. Clearing them here meant every
  // keystroke in the search box threw away what had been picked before it,
  // so nobody could select two people who did not appear in one search.
  const applyFilter = useCallback((next) => {
    setDraft(next);
    setAllMatching(false);
  }, []);

  const goToPage = (next) => {
    setPage(next);
    setParams(filterToSearchParams(urlFilter, { page: next }), { replace: true });
  };

  // UNTICKING ONE ROW UNDER «ALL MATCHING» NEEDS THE WHOLE LIST FIRST.
  // «انتخاب همهٔ ۱۱۰ مورد منطبق» sends the FILTER; the client holds only the
  // page it was clicked on. The first version flipped straight back to
  // explicit mode and toggled the one row — which, on page two, meant the
  // 109 others were never in `selected` at all: every row on the page went
  // blank except the one just clicked, and the count read «۱». Reported from
  // the running deployment on 2026-09-26.
  //
  // So the selection is MATERIALISED: the filter is re-run in pages of the
  // server's maximum, the ids become an explicit list, and the unticked
  // row is dropped from it. From then on it is an ordinary selection — the
  // filter can change and the 109 stay picked. Capped at the bulk limit,
  // which is also what the server would have refused past.
  const client = useApolloClient();
  const materialise = async (excluded) => {
    setMaterialising(true);
    try {
      const skip = new Set(excluded);
      const collected = [];
      const cap = Math.min(totalCount, bulkLimit);
      for (let p = 1; collected.length < cap; p += 1) {
        const { data: pageData } = await client.query({
          query: SEARCH_CONTACTS_QUERY,
          variables: {
            filter: filterInput,
            page: p,
            pageSize: SELECTION_FETCH_PAGE_SIZE,
            withFacets: false,
          },
          fetchPolicy: 'network-only',
        });
        const items = pageData?.searchContacts?.items ?? [];
        if (items.length === 0) break;
        collected.push(...items);
      }
      setSelected(collected.filter((c) => !skip.has(c.id)));
      setAllMatching(false);
    } catch {
      // Keep «all matching» rather than silently shrink the selection to one
      // page: the bulk bar still says N, and N is still what a send reaches.
    } finally {
      setMaterialising(false);
    }
  };

  const toggleRow = (contact) => {
    if (allMatching) {
      void materialise([contact.id]);
      return;
    }
    setSelected((current) =>
      current.some((c) => c.id === contact.id)
        ? current.filter((c) => c.id !== contact.id)
        : [...current, contact],
    );
  };

  // The header checkbox speaks for THIS PAGE only: it adds or removes the
  // rows on screen and leaves picks from other searches and pages alone.
  // Under «all matching» every page is selected, so it reads checked; unticking
  // it materialises the rest and drops this page.
  const pageSelected =
    allMatching ||
    (contacts.length > 0 && contacts.every((c) => selectedIds.includes(c.id)));
  const togglePage = (checked) => {
    if (allMatching) {
      if (!checked) void materialise(contacts.map((c) => c.id));
      return;
    }
    const onPage = new Set(contacts.map((c) => c.id));
    setSelected((current) => {
      const rest = current.filter((c) => !onPage.has(c.id));
      return checked ? [...rest, ...contacts] : rest;
    });
  };

  // The selector the bulk bar hands to the server. `allMatching` sends the
  // FILTER: the client never held those ids.
  const selector = allMatching
    ? { filter: filterInput }
    : { contactIds: selectedIds };

  const exportHref = useMemo(() => {
    const query = new URLSearchParams();
    query.set('filter', JSON.stringify(filterInput));
    // A download is a NAVIGATION, so it cannot carry an Authorization header
    // any more than EventSource can. Same trade as the pairing stream, same
    // note in todo.md: a short-lived token in a same-origin URL.
    const token = getAccessToken();
    if (token) query.set('token', token);
    return `${BFF_ORIGIN}/exports/contacts?${query}`;
  }, [filterInput]);

  return (
    <div className="flex h-screen">
      {showFilters && (
        <FilterPanel
          filter={draft}
          onChange={applyFilter}
          facets={facets}
          tags={tagData?.tags ?? []}
          lookups={lookups}
          searchRef={searchRef}
        />
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header
          className="flex h-toolbar shrink-0 items-center gap-3 border-b border-border
                     bg-surface px-6"
        >
          <Button variant="ghost" onClick={() => setShowFilters((v) => !v)}>
            <Filter size={14} aria-hidden="true" />
            {showFilters ? S.search.hideFilters : S.search.showFilters}
          </Button>
          <h1 className="text-lg font-bold">{S.contacts.title}</h1>
          <span className="text-sm text-text-muted">
            {formatCount(totalCount)} {S.search.results}
          </span>

          <Button variant="primary" onClick={() => setQuickAddOpen(true)}>
            <Plus size={14} aria-hidden="true" />
            {S.quickAdd.open}
          </Button>

          <div className="ms-auto flex items-center gap-2">
            <a
              href={exportHref}
              className="inline-flex h-control items-center gap-2 rounded border border-border
                         bg-surface px-4 text-base hover:bg-surface-hover"
            >
              <Download size={14} aria-hidden="true" />
              {S.search.bulkExport}
            </a>
          </div>
        </header>

        <SavedViewBar
          views={viewData?.savedViews ?? []}
          filter={draft}
          isAdmin={isAdmin}
          onApply={applyFilter}
          onChanged={refetchViews}
        />

        {(selected.length > 0 || allMatching) && (
          <BulkBar
            selector={selector}
            selectedCount={allMatching ? totalCount : selected.length}
            totalCount={totalCount}
            allMatching={allMatching}
            bulkLimit={bulkLimit}
            isAdmin={isAdmin}
            tags={tagData?.tags ?? []}
            // A REAL contact to preview the message against: the first one
            // picked, even if the current search no longer shows them. With
            // «select all matching» the client holds no ids at all, and the
            // first row on the page is somebody definitely in the set.
            sampleContact={allMatching ? contacts[0] : selected[0]}
            onSelectAllMatching={() => setAllMatching(true)}
            onClear={() => {
              setSelected([]);
              setAllMatching(false);
            }}
            onDone={() => {
              setSelected([]);
              setAllMatching(false);
              refetch();
            }}
          />
        )}

        <div className="min-h-0 flex-1 overflow-auto p-6">
          <MutationError error={deleteError} />
          {error && (
            <EmptyState
              title="خطا در دریافت اطلاعات"
              hint={error.message}
              className="text-danger"
            />
          )}
          {loading && contacts.length === 0 && (
            <div className="flex justify-center p-7">
              <Spinner />
            </div>
          )}
          {!loading && contacts.length === 0 && !error && (
            <EmptyState title={S.app.notFound} hint={S.contacts.emptyHint} />
          )}

          {contacts.length > 0 && (
            <Table>
              <THead>
                <TR>
                  <TH className="w-thumb">
                    <input
                      type="checkbox"
                      className="accent-primary"
                      aria-label={S.search.selectedCount.replace('{n}', '')}
                      checked={pageSelected}
                      disabled={materialising}
                      onChange={(event) => togglePage(event.target.checked)}
                    />
                  </TH>
                  <TH className="w-thumb">
                    <span className="sr-only">{S.contacts.columnPhoto}</span>
                  </TH>
                  <TH>{S.contacts.columns.name}</TH>
                  <TH>{S.contacts.columns.specialty}</TH>
                  <TH>{S.contacts.columns.primaryPhone}</TH>
                  <TH>{S.contacts.columns.assessment}</TH>
                  <TH>{S.contacts.columns.referrer}</TH>
                  <TH>{S.contacts.columns.tags}</TH>
                  <TH>{S.search.interactions}</TH>
                  <TH>{S.search.lastInteraction}</TH>
                  <TH className="w-thumb">
                    <span className="sr-only">{S.contacts.detail.deleteContact}</span>
                  </TH>
                </TR>
              </THead>
              <TBody>
                {contacts.map((contact) => (
                  // THE WHOLE ROW navigates, and it is the only thing here
                  // that does. Two cells used to carry their own handler, so
                  // clicking the specialty, the status or the phone number did
                  // nothing at all — a row that is clickable in two places out
                  // of nine reads as broken rather than as restrained.
                  //
                  // The checkbox cell below already stopped propagation,
                  // which is what this was always meant to be.
                  <TR
                    key={contact.id}
                    interactive
                    onClick={() => navigate(`/contacts/${contact.id}`)}
                  >
                    <TD onClick={(event) => event.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="accent-primary"
                        aria-label={contact.displayName}
                        checked={allMatching || selectedIds.includes(contact.id)}
                        disabled={materialising}
                        onChange={() => toggleRow(contact)}
                      />
                    </TD>
                    <TD>
                      {/* No photo falls back to a ♂/♀ glyph when the gender is
                          on file, and to the initials when it is not. */}
                      <ContactPhoto
                        name={contact.displayName}
                        url={contact.photoUrl}
                        gender={contact.gender}
                      />
                    </TD>
                    <TD className="font-medium">
                      {contact.displayName}
                      {contact.deletedAt && (
                        <span className="ms-2 text-xs text-danger">({S.app.delete})</span>
                      )}
                      {contact.messagingOptOut && (
                        <span
                          className="ms-2 inline-flex items-center gap-1 text-xs text-warning"
                          title={S.search.optedOut}
                        >
                          <MessageSquare size={11} aria-hidden="true" />
                        </span>
                      )}
                    </TD>
                    <TD className="text-text-muted">
                      {contact.specialty?.labelFa ?? contact.fieldOfWork?.labelFa ?? S.app.none}
                    </TD>
                    <TD dir="ltr" className="text-start tabular-nums">
                      {contact.primaryPhone
                        ? formatPhone(contact.primaryPhone.number)
                        : S.app.none}
                    </TD>
                    {/* «ارزیابی» and «برچسب‌ها» split the SAME tag list on
                        `isAssessment`, so one tag appears in exactly one
                        column. The flag is a property of the row (migration
                        0017) — nothing here compares a tag's name. */}
                    <TD>
                      <span className="flex flex-wrap gap-1">
                        {contact.tags
                          .filter((tag) => tag.isAssessment)
                          .map((tag) => (
                            <TagChip key={tag.id} name={tag.name} colorHex={tag.colorHex} />
                          ))}
                        {!contact.tags.some((tag) => tag.isAssessment) && (
                          <span className="text-text-muted">{S.app.none}</span>
                        )}
                      </span>
                    </TD>
                    <TD className="text-text-muted">
                      {/* Empty rather than invented: a contact nobody
                          introduced has no referrer, and «—» says so. */}
                      {contact.referrerName || S.app.none}
                    </TD>
                    <TD>
                      <span className="flex flex-wrap gap-1">
                        {contact.tags
                          .filter((tag) => !tag.isAssessment)
                          .map((tag) => (
                            <TagChip key={tag.id} name={tag.name} colorHex={tag.colorHex} />
                          ))}
                      </span>
                    </TD>
                    <TD className="tabular-nums text-text-muted">
                      {formatCount(contact.interactionCount ?? 0)}
                    </TD>
                    <TD className="text-text-muted">
                      {contact.lastInteractionAt
                        ? formatDate(contact.lastInteractionAt)
                        : S.search.never}
                    </TD>
                    {/* Delete from the list, not only from the profile — the
                        same soft delete and the same dialog. Stops propagation
                        like the checkbox cell: the row navigates, and this
                        cell must not. A contact already deleted has nothing
                        to delete. */}
                    <TD onClick={(event) => event.stopPropagation()}>
                      {!contact.deletedAt && (
                        <button
                          type="button"
                          onClick={() => setDeleting(contact)}
                          aria-label={`${S.contacts.detail.deleteContact} ${contact.displayName}`}
                          title={S.contacts.detail.deleteContact}
                          className="text-text-muted hover:text-danger"
                        >
                          <Trash2 size={14} aria-hidden="true" />
                        </button>
                      )}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </div>

        {pageCount > 1 && (
          <footer
            className="flex h-control shrink-0 items-center justify-center gap-4
                       border-t border-border bg-surface text-sm"
          >
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => goToPage(page - 1)}
              className="text-primary disabled:text-text-muted"
            >
              {S.app.previous}
            </button>
            <span className="text-text-muted">
              {S.app.pageOf(formatCount(page), formatCount(pageCount))}
            </span>
            <button
              type="button"
              disabled={page >= pageCount}
              onClick={() => goToPage(page + 1)}
              className="text-primary disabled:text-text-muted"
            >
              {S.app.next}
            </button>
          </footer>
        )}
      </div>

      <DeleteContactDialog
        contact={deleting}
        open={deleting != null}
        onOpenChange={(next) => {
          if (!next) setDeleting(null);
        }}
        onDeleted={() => {
          // A deleted contact leaves the list, so it leaves the selection too;
          // otherwise a bulk action would reach somebody no longer shown.
          const goneId = deleting?.id;
          setSelected((current) => current.filter((c) => c.id !== goneId));
          refetch();
        }}
        onError={setDeleteError}
      />

      <QuickAddDialog
        open={quickAddOpen}
        onOpenChange={setQuickAddOpen}
        onCreated={() => refetch()}
      />
    </div>
  );
}
